import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/lib/i18n";
import { http } from "@/lib/auth";
import { photoUrl, fmtKm, vehicleTitle } from "@/lib/vehicles";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ContactForm } from "@/components/site/ContactForm";

const Gallery = ({ photos, alt }) => {
  const [i, setI] = useState(0);
  return (
    <div>
      <div className="aspect-[4/3] overflow-hidden rounded-3xl border border-white/10 bg-white/5">
        {photos[i] && <img data-testid="vehicle-main-photo" src={photoUrl(photos[i])} alt={alt} className="h-full w-full object-cover" />}
      </div>
      {photos.length > 1 && (
        <div className="mt-3 grid grid-cols-5 gap-2">
          {photos.map((p, idx) => (
            <button key={p} data-testid={`vehicle-thumb-${idx}`} onClick={() => setI(idx)} className={`aspect-[4/3] overflow-hidden rounded-xl border-2 transition-colors ${idx === i ? "border-[#2F7BFF]" : "border-transparent opacity-70 hover:opacity-100"}`}>
              <img src={photoUrl(p)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function VehicleDetailPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const tv = t.vehicles;
  const [v, setV] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    http.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(() => setV(false));
  }, [id]);
  useEffect(() => { if (v) document.title = `${vehicleTitle(v)} · Autovantis`; }, [v]);

  const specs = v ? [
    [tv.spec.year, v.year], [tv.spec.mileage, fmtKm(v.mileage_km, lang)], [tv.spec.fuel, tv.fuel[v.fuel]],
    [tv.spec.transmission, tv.transmission[v.transmission]], [tv.spec.body, tv.body[v.body_type]],
    [tv.spec.power, v.power_hp ? `${v.power_hp} ${tv.hp}` : "—"], [tv.spec.color, v.color || "—"],
    [tv.spec.location, v.location || "—"], [tv.spec.quantity, v.quantity],
  ] : [];

  return (
    <div className="min-h-screen bg-[#060A14] text-white">
      <Header />
      <main className="mx-auto max-w-7xl px-5 pb-24 pt-10 lg:px-8">
        <Link to="/vehicles" data-testid="vehicle-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white"><ArrowLeft className="h-4 w-4" />{tv.back}</Link>
        {v === null ? (
          <div className="grid h-96 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#2F7BFF]" /></div>
        ) : v === false ? (
          <p data-testid="vehicle-not-found" className="py-24 text-center text-slate-400">{tv.notFound}</p>
        ) : (
          <div data-testid="vehicle-detail" className="mt-6 grid gap-10 lg:grid-cols-[1.3fr_1fr]">
            <Gallery photos={v.photos} alt={vehicleTitle(v)} />
            <div>
              {v.is_sample && <span className="mb-3 inline-block rounded-full bg-white/90 px-3 py-1 text-xs font-bold uppercase text-[#0B1224]">{tv.sample}</span>}
              <h1 data-testid="vehicle-title" className="font-display text-4xl font-extrabold tracking-tight">{vehicleTitle(v)}</h1>
              {v.variant && <p className="mt-1 text-lg text-slate-300">{v.variant}</p>}
              <div className="mt-6 rounded-2xl border border-[#2F7BFF]/30 bg-[#2F7BFF]/10 p-5">
                <div data-testid="vehicle-price" className="font-display text-3xl font-extrabold">{tv.requestPrice}</div>
                <div className="mt-1 text-sm text-slate-300">{tv.priceNote}{v.status === "reserved" ? ` · ${tv.status.reserved}` : ""}</div>
              </div>
              <dl className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10">
                {specs.map(([k, val]) => (
                  <div key={k} className="bg-[#0B1224] p-4"><dt className="text-xs uppercase tracking-wider text-slate-400">{k}</dt><dd className="mt-1 font-semibold">{val}</dd></div>
                ))}
              </dl>
              <button data-testid="vehicle-request-button" onClick={() => setOpen(true)} className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2F7BFF] px-8 py-4 font-bold text-white shadow-[0_12px_36px_-10px_rgba(47,123,255,.9)] transition-colors hover:bg-[#1F63E0]">
                <Send className="h-4 w-4" />{tv.request}
              </button>
              {v.description && <p className="mt-8 whitespace-pre-wrap text-[15px] leading-relaxed text-slate-300">{v.description}</p>}
            </div>
          </div>
        )}
      </main>
      <Footer />
      {v && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid="vehicle-request-dialog" className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#0B1224] text-white sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{tv.requestTitle}</DialogTitle>
              <p className="text-sm text-slate-400">{vehicleTitle(v)} {v.variant} · {v.year}</p>
            </DialogHeader>
            <ContactForm vehicle={v} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
